import { create } from 'zustand';
import { prop, type PropsElement, type PropsFile } from '@manudev/assistant';
import { net } from '@/lib/net';
import { hero } from '@/state/tour';
import { MODELS_BASE } from './preload';

export { prop, type PropsElement, type PropsFile };

export interface LayerStats {
  bytes: number;
  elements: number;
  triangles: number;
  // elements per material preset, where the pipeline wrote them
  presets?: Record<string, number>;
}

export interface HeroData {
  props: Record<string, PropsFile>;
  stats: Record<string, LayerStats>;
  byId: Map<number, PropsElement & { layer: string }>;
  raw: Record<number, string>;
}

interface HeroDataState {
  data: HeroData | null;
  loading: boolean;
  load: () => Promise<void>;
  // After an edit: the originals still in the store keep their pipeline data, what was built
  // comes from the store, and originals the visitor deleted are gone (they return on undo).
  sync: (store: PropsElement[], newIds: number[]) => void;
  removed: number[];
}

const STORE_LAYER = 'store';

export const useHeroData = create<HeroDataState>((set, get) => ({
  data: null,
  loading: false,
  removed: [],
  sync: (store, newIds) =>
    set((s) => {
      if (!s.data) return s;
      const present = new Set(store.map((e) => e.expressID));
      const built = new Set(newIds);
      const byId = new Map<number, PropsElement & { layer: string }>();
      const removed: number[] = [];
      for (const [layer, file] of Object.entries(s.data.props)) {
        // only layers the store carries can lose elements; the MEP layers come from other files
        const inStore = file.elements.filter((el) => present.has(el.expressID)).length;
        const editable = inStore * 2 >= file.elements.length;
        for (const el of file.elements) {
          if (!editable || present.has(el.expressID)) byId.set(el.expressID, { ...el, layer });
          else removed.push(el.expressID);
        }
      }
      for (const el of store)
        if (built.has(el.expressID)) byId.set(el.expressID, { ...el, layer: STORE_LAYER });
      return { data: { ...s.data, byId }, removed };
    }),
  load: async () => {
    if (get().data || get().loading) return;
    set({ loading: true });
    try {
      const props: Record<string, PropsFile> = {};
      await Promise.all(
        hero.layers
          .filter((l) => l.props)
          .map(async (l) => {
            const file = await fetchJson<PropsFile>(MODELS_BASE + l.props);
            if (file) props[l.id] = file;
          }),
      );
      const byId = new Map<number, PropsElement & { layer: string }>();
      for (const [layer, file] of Object.entries(props))
        for (const el of file.elements) byId.set(el.expressID, { ...el, layer });
      const raw =
        (hero.raw && (await fetchJson<Record<number, string>>(MODELS_BASE + hero.raw))) || {};
      const manifest =
        (await fetchJson<Record<string, LayerStats>>(MODELS_BASE + 'manifest.json')) ?? {};
      const stats: Record<string, LayerStats> = {};
      for (const l of hero.layers) if (manifest[l.id]) stats[l.id] = manifest[l.id]!;
      set({ data: { props, byId, raw, stats }, loading: false });
    } catch (err) {
      console.warn('[hero] props failed to load; data lenses degrade gracefully', err);
      set({ loading: false });
    }
  },
}));

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await net(url);
    if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) {
      console.warn('[hero] missing sidecar', url);
      return null;
    }
    return (await res.json()) as T;
  } catch (err) {
    console.warn('[hero] sidecar failed', url, err);
    return null;
  }
}
